/**
 * Whether the player's screen is QUIET: nothing to read, nothing open, nothing fighting them. The
 * server asks before it puts up anything optional -- the feedback rabbit waits for quiet to come, and
 * runs off the moment it ends -- because only the client knows what is on the player's screen.
 *
 * It is a signal, not a decision: GuiManager feeds it, every frame, the reason the screen is busy (or
 * none), and the server is told only when the answer changes (the SCREEN_QUIET player event). Busy
 * goes out AT ONCE, so whatever is waiting on quiet can get out of the way of what just started. Quiet
 * goes out only once the screen has STAYED quiet for QUIET_SETTLE_MS: the gaps between the Lord's
 * held lines, a toast's fade, a menu closed on the way to another, are not quiet moments, and an
 * invitation that arrived in one would be run off a second later.
 */

/** How long the screen must stay quiet before it is called quiet. */
export const QUIET_SETTLE_MS = 4000
/** How long after the player was hurt the screen still counts as a fight. */
export const HURT_BUSY_MS = 10000

export class QuietWatch {
    private quietSince?: number
    private reported?: boolean
    private lastReason: string | null = "starting"

    /**
     * One frame. `busy` is why the screen is busy, or null. Returns the state to report when it has
     * changed, and undefined otherwise.
     */
    update = (now: number, busy: string | null): boolean | undefined => {
        this.lastReason = busy
        if (busy) {
            this.quietSince = undefined
            if (this.reported !== false) {
                this.reported = false
                return false
            }
            return undefined
        }
        if (this.quietSince === undefined) this.quietSince = now
        if (now - this.quietSince >= QUIET_SETTLE_MS && this.reported !== true) {
            this.reported = true
            return true
        }
        return undefined
    }

    /**
     * Forget what was reported, so the current state is sent again: a new room's campaigns have
     * never been told, and a new socket's server has never heard of us.
     */
    reset = () => {
        this.reported = undefined
        this.quietSince = undefined
    }

    isQuiet = (): boolean => this.reported === true

    /** Why the screen was last busy, or null: for `window.quietWatch` in the console. */
    reason = (): string | null => this.lastReason
}
